import React, { Fragment, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ExpandableSection, SpaceBetween, Box, Button, Popover, Link, Alert } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { DateTime } from 'luxon';
import { ResolvedMatch, TimeSlot, AttendeePriority } from '../interfaces/interface';
import { ProfilePicture } from '../components/ProfilePicture';
import { EmployeePopoverCard } from '../components/attendees/EmployeePopoverCard';
import { PRIORITY_COLORS } from '../constants/EAHuddleConstants';
import { getUserTimezone } from 'src/utilities/DateTimeUtilities';
import { generatePhoneToolUrl } from '../../../../constants/AppConstants';
import './TimezoneConverterContainer.scss';

// ── Constants ──────────────────────────────────────────────────────────

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const LABEL_W = 220;
const HEADER_H = 36;
const CELL_H = 44;

// ── Color palette ──────────────────────────────────────────────────────

export const TZ_COLORS = {
  day: { bg: '#DBEAFE', fg: '#1E3A5F', border: '#93C5FD' },
  night: { bg: '#1E293B', fg: '#ffffff', border: '#334155' },
  meeting: { bg: '#7C3AED', fg: '#ffffff', border: '#6D28D9' },
  selected: { bg: '#16A34A', fg: '#ffffff', border: '#15803D' },
  proposed: { border: '#F97316' }
} as const;

// ── Types ──────────────────────────────────────────────────────────────

interface IndividualRow {
  type: 'individual';
  key: string;
  attendee: ResolvedMatch;
  timezone: string;
}

interface GroupedRow {
  type: 'grouped';
  key: string;
  timezone: string;
  attendees: ResolvedMatch[];
  requiredCount: number;
  optionalCount: number;
}

type GridRow = IndividualRow | GroupedRow;

export interface SlotPosition {
  startFrac: number;
  durationHours: number;
}

// ── Helpers ────────────────────────────────────────────────────────────

function splitHour(hourVal: number): { h: number; period: string } {
  const hour = ((hourVal % 24) + 24) % 24;
  const period = hour < 12 ? 'am' : 'pm';
  const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return { h, period };
}

function formatHourShort(utcH: number): string {
  const { h, period } = splitHour(utcH);
  return `${h}${period}`;
}

function formatFractionalHour(frac: number): string {
  const hour = Math.floor(frac);
  const min = Math.round((frac - hour) * 60);
  const { h, period } = splitHour(hour);
  return min === 0 ? `${h}${period}` : `${h}:${String(min).padStart(2, '0')}${period}`;
}

function formatEndTime(startUtcHour: number, durationMinutes: number): string {
  const totalMins = startUtcHour * 60 + durationMinutes;
  const endHour = Math.floor(totalMins / 60) % 24;
  const endMin = totalMins % 60;
  const { h, period } = splitHour(endHour);
  return endMin === 0 ? `${h}${period}` : `${h}:${String(endMin).padStart(2, '0')}${period}`;
}

function getTimezoneAbbr(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
      hour: 'numeric',
      hour12: false
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || timezone;
  } catch {
    return timezone;
  }
}

export function cellColors(localHour: number): { bg: string; fg: string } {
  if (localHour < 8 || localHour >= 17) return { bg: TZ_COLORS.night.bg, fg: TZ_COLORS.night.fg };
  return { bg: TZ_COLORS.day.bg, fg: TZ_COLORS.day.fg };
}

function buildGroupLabel(requiredCount: number, optionalCount: number): string {
  const parts: string[] = [];
  if (requiredCount > 0) parts.push(`${requiredCount} Required`);
  if (optionalCount > 0) parts.push(`${optionalCount} Optional`);
  return parts.join(', ');
}

/** Convert a time slot's start/end into fractional hours in the target timezone. */
export function computeSlotPosition(date: string, startTime: string, endTime: string, slotTz: string, targetTz: string): SlotPosition {
  const startDt = DateTime.fromFormat(`${date} ${startTime}`, 'yyyy-MM-dd HH:mm', { zone: slotTz }).setZone(targetTz);
  let endDt = DateTime.fromFormat(`${date} ${endTime}`, 'yyyy-MM-dd HH:mm', { zone: slotTz }).setZone(targetTz);
  if (endDt <= startDt) endDt = endDt.plus({ days: 1 });
  return {
    startFrac: startDt.hour + startDt.minute / 60,
    durationHours: endDt.diff(startDt, 'minutes').minutes / 60
  };
}

/** Compute the calendar day offset between the user's timezone and an attendee's timezone for a given slot. */
export function computeDayOffset(date: string, startTime: string, slotTz: string, attendeeTz: string, userTz: string): string {
  const effectiveTz = slotTz || userTz;
  const slotInUser = DateTime.fromFormat(`${date} ${startTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(userTz);
  const slotInAttendee = DateTime.fromFormat(`${date} ${startTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(attendeeTz);
  const userDate = slotInUser.toFormat('yyyy-MM-dd');
  const attendeeDate = slotInAttendee.toFormat('yyyy-MM-dd');
  const diff = DateTime.fromISO(attendeeDate).diff(DateTime.fromISO(userDate), 'days').days;
  if (diff > 0) return `+${diff}d`;
  if (diff < 0) return `${diff}d`;
  return '';
}

/** Check if a click at the given hour with the given duration would overflow past midnight. */
export function canClickHour(hoveredCol: number, durationMinutes: number): boolean {
  return hoveredCol + durationMinutes / 60 < 24;
}

/** Compute the hover overlay width in pixels, clamped to remaining hours in the day. */
export function computeOverlayWidth(hoveredCol: number | null, durationMinutes: number, cellW: number): number {
  if (hoveredCol === null) return 0;
  const durationHours = durationMinutes / 60;
  return cellW * Math.min(durationHours, 24 - hoveredCol);
}

// ── Exported helpers for grid date logic ────────────────────────────────

interface SlotLike {
  date: string;
  startTime: string;
  timezone: string;
}

export function computeGridDate(
  proposedSlot: { date: string } | null | undefined,
  selectedSlot: SlotLike | null | undefined,
  userTz: string
): string {
  if (proposedSlot?.date) return proposedSlot.date;
  if (selectedSlot) {
    return DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.startTime}`, 'yyyy-MM-dd HH:mm', { zone: selectedSlot.timezone || userTz })
      .setZone(userTz)
      .toFormat('yyyy-MM-dd');
  }
  return DateTime.now().setZone(userTz).toFormat('yyyy-MM-dd');
}

export function isSelectedSlotOnGridDate(selectedSlot: SlotLike | null | undefined, gridDate: string, userTz: string): boolean {
  if (!selectedSlot) return false;
  const slotDateInUserTz = DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.startTime}`, 'yyyy-MM-dd HH:mm', {
    zone: selectedSlot.timezone || userTz
  })
    .setZone(userTz)
    .toFormat('yyyy-MM-dd');
  return slotDateInUserTz === gridDate;
}

// ── Props ──────────────────────────────────────────────────────────────

export interface TimeWindowClickData {
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
}

interface TimezoneConverterContainerProps {
  resolvedAttendees: ResolvedMatch[];
  duration: number;
  activeTimeSlots: TimeSlot[];
  selectedSlotId?: string | null;
  proposedSlot?: { date: string; startTime: string; endTime: string; timezone: string } | null;
  hoveredSlotId?: string | null;
  onTimeWindowClick?: (data: TimeWindowClickData) => void;
  onTimeWindowDoubleClick?: (data: TimeWindowClickData) => void;
  isReadOnly?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────

export const TimezoneConverterContainer: React.FC<TimezoneConverterContainerProps> = ({
  resolvedAttendees,
  duration,
  activeTimeSlots,
  selectedSlotId,
  proposedSlot,
  hoveredSlotId,
  onTimeWindowClick,
  onTimeWindowDoubleClick,
  isReadOnly = false
}) => {
  const [expanded, setExpanded] = useState(true);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const browserTz = useMemo(() => getUserTimezone(), []);
  const userTz = proposedSlot?.timezone || browserTz;
  const userTzAbbr = useMemo(() => DateTime.now().setZone(userTz).toFormat('ZZZZ'), [userTz]);
  const [nowLocalHour, setNowLocalHour] = useState(() => DateTime.now().setZone(userTz).hour);
  const [nowMinStartFrac, setNowMinStartFrac] = useState(() => {
    const now = DateTime.now().setZone(userTz);
    return (Math.ceil((now.hour * 60 + now.minute) / 15) * 15) / 60;
  });

  useEffect(() => {
    const update = () => {
      const now = DateTime.now().setZone(userTz);
      setNowLocalHour(now.hour);
      setNowMinStartFrac((Math.ceil((now.hour * 60 + now.minute) / 15) * 15) / 60);
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [userTz]);

  // ── Grid rows: critical = individual, required/optional = grouped by timezone ──

  const gridRows = useMemo<GridRow[]>(() => {
    const criticalRows: IndividualRow[] = [];
    const groupMap = new Map<string, ResolvedMatch[]>();

    for (const a of resolvedAttendees) {
      const tz = a.selectedEmployee?.timeZoneId;
      if (!tz || removedIds.has(a.id)) continue;

      if (a.priority === AttendeePriority.Critical) {
        criticalRows.push({ type: 'individual', key: a.id, attendee: a, timezone: tz });
      } else {
        const existing = groupMap.get(tz) || [];
        existing.push(a);
        groupMap.set(tz, existing);
      }
    }

    const groupedRows: GridRow[] = [];
    for (const [timezone, attendees] of groupMap) {
      groupedRows.push({
        type: 'grouped',
        key: `group-${timezone}`,
        timezone,
        attendees,
        requiredCount: attendees.filter((a) => a.priority === AttendeePriority.Required).length,
        optionalCount: attendees.filter((a) => a.priority === AttendeePriority.Optional).length
      });
    }

    return [...criticalRows, ...groupedRows];
  }, [resolvedAttendees, removedIds]);

  // ── Slot lookups ──────────────────────────────────────────────────────

  const selectedSlot = useMemo(
    () => (selectedSlotId ? activeTimeSlots.find((s) => s.slotId === selectedSlotId) : undefined),
    [selectedSlotId, activeTimeSlots]
  );

  const hoveredSlot = useMemo(
    () => (hoveredSlotId ? activeTimeSlots.find((s) => s.slotId === hoveredSlotId) : undefined),
    [hoveredSlotId, activeTimeSlots]
  );

  // ── Grid date (what date the converter is showing) ─────────────────

  const gridDate = useMemo(() => computeGridDate(proposedSlot, selectedSlot, userTz), [proposedSlot, selectedSlot, userTz]);

  const isGridToday = useMemo(() => gridDate === DateTime.now().setZone(userTz).toFormat('yyyy-MM-dd'), [gridDate, userTz]);

  // ── Slot positions (fractional hours in user's timezone) ──────────────

  const selectedSlotOnGridDate = useMemo(() => isSelectedSlotOnGridDate(selectedSlot, gridDate, userTz), [selectedSlot, gridDate, userTz]);

  const selectedSlotPosition = useMemo<SlotPosition | null>(() => {
    if (!selectedSlot || !selectedSlotOnGridDate) return null;
    return computeSlotPosition(selectedSlot.date, selectedSlot.startTime, selectedSlot.endTime, selectedSlot.timezone || userTz, userTz);
  }, [selectedSlot, selectedSlotOnGridDate, userTz]);

  const proposedSlotPosition = useMemo<SlotPosition | null>(() => {
    if (!proposedSlot || !proposedSlot.startTime || !proposedSlot.endTime) return null;
    return computeSlotPosition(proposedSlot.date, proposedSlot.startTime, proposedSlot.endTime, proposedSlot.timezone, userTz);
  }, [proposedSlot, userTz]);

  const hoveredSlotPosition = useMemo<SlotPosition | null>(() => {
    if (!hoveredSlot) return null;
    return computeSlotPosition(hoveredSlot.date, hoveredSlot.startTime, hoveredSlot.endTime, hoveredSlot.timezone || userTz, userTz);
  }, [hoveredSlot, userTz]);

  // ── Selected slot label ───────────────────────────────────────────────

  const selectedSlotLabel = useMemo(() => {
    if (!selectedSlot) return '';
    const effectiveTz = selectedSlot.timezone || userTz;
    const start = DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.startTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(userTz);
    const end = DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.endTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(userTz);
    return `${start.toFormat('EEEE, MMMM d, yyyy')} · ${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')} ${start.toFormat('ZZZZ')}`;
  }, [selectedSlot, userTz]);

  const selectedSlotOverlayLabel = useMemo(() => {
    if (!selectedSlotPosition || !selectedSlot) return '';
    const endFrac = selectedSlotPosition.startFrac + selectedSlotPosition.durationHours;
    return `${formatFractionalHour(selectedSlotPosition.startFrac)} → ${formatFractionalHour(endFrac)} ${userTzAbbr}`;
  }, [selectedSlotPosition, selectedSlot, userTzAbbr]);

  const hoveredSlotOverlayLabel = useMemo(() => {
    if (!hoveredSlotPosition || !hoveredSlot) return '';
    const endFrac = hoveredSlotPosition.startFrac + hoveredSlotPosition.durationHours;
    return `${formatFractionalHour(hoveredSlotPosition.startFrac)} → ${formatFractionalHour(endFrac)} ${userTzAbbr}`;
  }, [hoveredSlotPosition, hoveredSlot, userTzAbbr]);

  const proposedSlotOverlayLabel = useMemo(() => {
    if (!proposedSlotPosition || !proposedSlot) return '';
    const endFrac = proposedSlotPosition.startFrac + proposedSlotPosition.durationHours;
    return `${formatFractionalHour(proposedSlotPosition.startFrac)} → ${formatFractionalHour(endFrac)} ${userTzAbbr}`;
  }, [proposedSlotPosition, proposedSlot, userTzAbbr]);

  /** Check if a user-tz hour falls within any active overlay range (excludes past hours) */
  const isHourInOverlay = useCallback(
    (userH: number): boolean => {
      if (isGridToday && userH < Math.floor(nowMinStartFrac)) return false;
      const slotPos = hoveredSlotPosition ?? selectedSlotPosition;
      if (slotPos) {
        const slotStart = Math.floor(slotPos.startFrac);
        const slotEnd = Math.ceil(slotPos.startFrac + slotPos.durationHours);
        if (userH >= slotStart && userH < slotEnd) return true;
      }
      return false;
    },
    [hoveredSlotPosition, selectedSlotPosition, isGridToday, nowMinStartFrac]
  );

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleRemove = useCallback((id: string) => {
    setRemovedIds((prev) => new Set(prev).add(id));
  }, []);

  const handleRemoveGroup = useCallback((attendees: ResolvedMatch[]) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      attendees.forEach((a) => next.add(a.id));
      return next;
    });
  }, []);

  const handleResetRemoved = useCallback(() => setRemovedIds(new Set()), []);

  // ── Grid hover (meeting window preview) ───────────────────────────────

  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);

  const userHourToAttendeeTime = useCallback(
    (userLocalH: number, attendeeTz: string): { hour: number; minute: number } => {
      const dt = DateTime.fromFormat(gridDate, 'yyyy-MM-dd', { zone: userTz }).set({ hour: userLocalH }).setZone(attendeeTz);
      return { hour: dt.hour, minute: dt.minute };
    },
    [userTz, gridDate]
  );

  const [cellW, setCellW] = useState(0);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setCellW((el.getBoundingClientRect().width - LABEL_W) / 24);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [gridRows.length]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const el = scrollRef.current;
      if (!el || cellW <= 0) return;
      const x = e.clientX - el.getBoundingClientRect().left + el.scrollLeft - LABEL_W;
      // Snap to 15-min increments (0.25 hour)
      const rawFrac = x / cellW;
      const snapped = Math.floor(rawFrac * 4) / 4;
      const durationHours = duration / 60;
      // Clamp: must fit within 0–24 and not start before next available 15-min boundary
      if (snapped < 0 || snapped + durationHours > 24 || (isGridToday && snapped < nowMinStartFrac)) {
        setHoveredCol(null);
      } else {
        setHoveredCol(snapped);
      }
    },
    [cellW, isGridToday, nowMinStartFrac, duration]
  );

  const handleMouseLeave = useCallback(() => setHoveredCol(null), []);

  const buildTimeWindowData = useCallback((): TimeWindowClickData | null => {
    if (hoveredCol === null) return null;
    const startDt = DateTime.fromFormat(gridDate, 'yyyy-MM-dd', { zone: userTz })
      .startOf('day')
      .plus({ minutes: hoveredCol * 60 });
    const endDt = startDt.plus({ minutes: duration });
    return {
      date: startDt.toFormat('yyyy-MM-dd'),
      startTime: startDt.toFormat('HH:mm'),
      endTime: endDt.toFormat('HH:mm'),
      timezone: userTz
    };
  }, [hoveredCol, duration, userTz, gridDate]);

  const handleGridClick = useCallback(() => {
    const data = buildTimeWindowData();
    if (data) onTimeWindowClick?.(data);
  }, [buildTimeWindowData, onTimeWindowClick]);

  const handleGridDoubleClick = useCallback(() => {
    const data = buildTimeWindowData();
    if (data) onTimeWindowDoubleClick?.(data);
  }, [buildTimeWindowData, onTimeWindowDoubleClick]);

  const getDayOffset = useCallback(
    (timezone: string): string => {
      if (!selectedSlot) return '';
      return computeDayOffset(selectedSlot.date, selectedSlot.startTime, selectedSlot.timezone, timezone, userTz);
    },
    [selectedSlot, userTz]
  );

  // ── Overlay rendering helper ──────────────────────────────────────────

  const renderSlotOverlay = (position: SlotPosition | null, className: string, label?: string): React.ReactNode => {
    if (!position || cellW <= 0) return null;
    return (
      <div
        className={className}
        style={{
          top: HEADER_H,
          left: LABEL_W + position.startFrac * cellW,
          width: position.durationHours * cellW,
          height: gridRows.length * CELL_H
        }}
      >
        {label && <div className="tz-grid-overlay-label">{label}</div>}
      </div>
    );
  };

  // ── Early return ──────────────────────────────────────────────────────

  if (resolvedAttendees.length === 0) return null;

  // ── Hover overlay values ──────────────────────────────────────────────

  const overlayLeft = hoveredCol !== null ? LABEL_W + hoveredCol * cellW : 0;
  const overlayWidth = hoveredCol !== null ? (duration / 60) * cellW : 0;
  const overlayLabel =
    hoveredCol !== null ? `${formatFractionalHour(hoveredCol)} → ${formatFractionalHour(hoveredCol + duration / 60)} ${userTzAbbr}` : null;

  return (
    <ExpandableSection
      variant="container"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerText="Time Zone Converter"
      headerDescription="Visualize attendee time zones to find the best meeting window."
      headerActions={
        removedIds.size > 0 && !isReadOnly ? (
          <Button variant="link" onClick={handleResetRemoved}>
            Reset
          </Button>
        ) : undefined
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {selectedSlot && selectedSlotOnGridDate && (
          <Box variant="small">
            <Box fontWeight="bold" variant="span">
              Selected Slot:{' '}
            </Box>
            <Box fontWeight="bold" variant="span" color="text-status-success">
              {selectedSlotLabel}
            </Box>
          </Box>
        )}

        {gridRows.length === 0 ? (
          <Box variant="small" color="text-body-secondary" textAlign="center" padding={{ vertical: 'm' }}>
            No attendees with timezone data available.
          </Box>
        ) : (
          <>
            <div
              ref={scrollRef}
              className="tz-grid-scroll"
              role="grid"
              tabIndex={isReadOnly ? -1 : 0}
              aria-label="Timezone comparison grid"
              aria-readonly={isReadOnly || undefined}
              onMouseMove={isReadOnly ? undefined : handleMouseMove}
              onMouseLeave={isReadOnly ? undefined : handleMouseLeave}
              onClick={isReadOnly ? undefined : handleGridClick}
              onDoubleClick={isReadOnly ? undefined : handleGridDoubleClick}
              style={isReadOnly ? { cursor: 'default' } : undefined}
              onKeyDown={
                isReadOnly
                  ? undefined
                  : (e) => {
                      const step = 0.25;
                      const maxStart = 24 - duration / 60;
                      if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        setHoveredCol((prev) => (prev === null ? 0 : Math.min(prev + step, maxStart)));
                      } else if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        setHoveredCol((prev) => (prev === null ? 0 : Math.max(prev - step, 0)));
                      } else if (e.key === 'Escape') {
                        setHoveredCol(null);
                      } else if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleGridClick();
                      }
                    }
              }
            >
              <div ref={gridRef} className="tz-grid" style={{ gridTemplateColumns: `${LABEL_W}px repeat(24, minmax(0, 1fr))` }}>
                {/* Header: label cell */}
                <div className="tz-grid-header-label">
                  <div>{userTzAbbr}</div>
                  <div className="tz-grid-header-date">{DateTime.fromFormat(gridDate, 'yyyy-MM-dd').toFormat('EEE, MMM d')}</div>
                </div>

                {/* Header: hour cells */}
                {HOURS.map((h) => {
                  const { h: hNum, period } = splitHour(h);
                  const isNow = h === nowLocalHour;
                  const isPast = isGridToday && h < Math.floor(nowMinStartFrac);
                  return (
                    <div
                      key={h}
                      className={`tz-grid-header-cell ${isNow ? 'tz-grid-header-cell--now' : ''}${isPast ? ' tz-grid-header-cell--past' : ''}`}
                    >
                      <span className="tz-grid-hour-num">{hNum}</span>
                      <span className="tz-grid-hour-period">{period}</span>
                      {isNow && <span className="tz-grid-now-dot" />}
                    </div>
                  );
                })}

                {/* Data rows */}
                {gridRows.map((row, ri) => {
                  const isLast = ri === gridRows.length - 1;
                  const { timezone } = row;
                  const abbr = getTimezoneAbbr(timezone);
                  const dayOffset = getDayOffset(timezone);

                  return (
                    <Fragment key={row.key}>
                      {/* Label cell */}
                      <div className={`tz-grid-label ${isLast ? '' : 'tz-grid-label--bordered'}`}>
                        <div className="tz-grid-label-content">
                          <span className="tz-grid-tz-abbr">{abbr}</span>
                          {row.type === 'individual' ? (
                            <>
                              <EmployeePopoverCard loginName={row.attendee.selectedEmployee!.login_name} renderWithPortal>
                                <ProfilePicture
                                  loginName={row.attendee.selectedEmployee!.login_name}
                                  name={row.attendee.selectedEmployee!.displayName || row.attendee.selectedEmployee!.employee_name}
                                  size="small"
                                />
                              </EmployeePopoverCard>
                              <div className="tz-grid-name-wrapper">
                                <a
                                  href={generatePhoneToolUrl(row.attendee.selectedEmployee!.login_name)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tz-grid-name-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row.attendee.selectedEmployee!.displayName || row.attendee.selectedEmployee!.employee_name}
                                </a>
                                {dayOffset && <span className="tz-grid-day-offset">{dayOffset}</span>}
                              </div>
                            </>
                          ) : (
                            <div className="tz-grid-name-wrapper" onClick={(e) => e.stopPropagation()}>
                              <Popover
                                triggerType="custom"
                                position="bottom"
                                size="large"
                                dismissButton
                                renderWithPortal
                                content={
                                  <SpaceBetween direction="vertical" size="xs">
                                    <Box fontWeight="bold" fontSize="body-s">
                                      {buildGroupLabel(row.requiredCount, row.optionalCount)} — {abbr}
                                    </Box>
                                    {row.attendees.map((a) => {
                                      const colors = PRIORITY_COLORS[a.priority];
                                      return (
                                        <div key={a.id} className="tz-grid-popover-row">
                                          <EmployeePopoverCard loginName={a.selectedEmployee!.login_name} renderWithPortal>
                                            <Avatar
                                              username={a.selectedEmployee!.login_name}
                                              size={AvatarSize.ExtraSmall}
                                              imageAltText={`Profile image of ${a.selectedEmployee!.login_name}`}
                                            />
                                          </EmployeePopoverCard>
                                          <div className="tz-grid-popover-badge">
                                            <span className="priority-badge" style={{ backgroundColor: colors.background, color: colors.color }}>
                                              {a.priority.charAt(0).toUpperCase() + a.priority.slice(1)}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </SpaceBetween>
                                }
                              >
                                <Link variant="info" fontSize="body-s">
                                  {buildGroupLabel(row.requiredCount, row.optionalCount)}
                                </Link>
                              </Popover>
                              {dayOffset && <span className="tz-grid-day-offset">{dayOffset}</span>}
                            </div>
                          )}
                        </div>
                        {!isReadOnly && (
                          <button
                            className="tz-grid-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              row.type === 'individual' ? handleRemove(row.attendee.id) : handleRemoveGroup(row.attendees);
                            }}
                            aria-label={
                              row.type === 'individual'
                                ? `Remove ${row.attendee.selectedEmployee?.displayName || row.attendee.selectedEmployee?.employee_name} from grid`
                                : `Remove ${abbr} group from grid`
                            }
                            type="button"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {/* Hour cells */}
                      {HOURS.map((userH) => {
                        const { hour: localH, minute: localM } = userHourToAttendeeTime(userH, timezone);
                        const { bg, fg } = cellColors(localH);
                        const { h: hNum, period } = splitHour(localH);
                        const isPast = isGridToday && userH < Math.floor(nowMinStartFrac);
                        const inSlot =
                          isHourInOverlay(userH) ||
                          (hoveredCol !== null && !isPast && userH >= Math.floor(hoveredCol) && userH < Math.ceil(hoveredCol + duration / 60));
                        return (
                          <div
                            key={userH}
                            className={`tz-grid-cell ${isLast ? '' : 'tz-grid-cell--bordered'}${isPast ? ' tz-grid-cell--past' : ''}`}
                            style={{ background: bg, color: inSlot ? '#ffffff' : fg }}
                          >
                            <span>{localM ? `${hNum}:${String(localM).padStart(2, '0')}` : hNum}</span>
                            <span className="tz-grid-cell-period">{period}</span>
                          </div>
                        );
                      })}
                    </Fragment>
                  );
                })}

                {/* Hover overlay */}
                {hoveredCol !== null && (
                  <div
                    className="tz-grid-overlay"
                    style={{
                      top: HEADER_H,
                      left: overlayLeft,
                      width: overlayWidth,
                      height: gridRows.length * CELL_H
                    }}
                  >
                    <div className="tz-grid-overlay-label">{overlayLabel}</div>
                  </div>
                )}

                {/* Slot overlays */}
                {(() => {
                  const activeSlotPos = hoveredSlotPosition ?? selectedSlotPosition;
                  return renderSlotOverlay(
                    activeSlotPos,
                    `tz-grid-selected-overlay${
                      activeSlotPos && (Math.floor(activeSlotPos.startFrac) < 8 || Math.floor(activeSlotPos.startFrac) >= 17)
                        ? ' tz-grid-selected-overlay--night'
                        : ''
                    }`,
                    hoveredSlotOverlayLabel || selectedSlotOverlayLabel
                  );
                })()}
                {renderSlotOverlay(proposedSlotPosition, 'tz-grid-proposed-overlay', proposedSlotOverlayLabel)}
              </div>
            </div>

            {/* Legend */}
            <div className="tz-grid-legend">
              {[
                { bg: TZ_COLORS.day.bg, border: TZ_COLORS.day.border, label: 'Day (8 AM – 5 PM)' },
                { bg: TZ_COLORS.night.bg, border: TZ_COLORS.night.border, label: 'Night (5 PM – 8 AM)' },
                { bg: TZ_COLORS.meeting.bg, border: TZ_COLORS.meeting.border, label: `Meeting Window (${duration}m)` },
                ...(selectedSlot
                  ? [{ bg: TZ_COLORS.selected.bg, border: TZ_COLORS.selected.border, label: `Selected Slot (${selectedSlotLabel})` }]
                  : []),
                ...(proposedSlotPosition ? [{ bg: 'transparent', border: TZ_COLORS.proposed.border, label: 'Proposed Window', dashed: true }] : [])
              ].map(({ bg, border, label, dashed }) => (
                <div key={label} className="tz-grid-legend-item">
                  <div className="tz-grid-legend-swatch" style={{ background: bg, borderColor: border, borderStyle: dashed ? 'dashed' : 'solid' }} />
                  <Box variant="small" color="text-body-secondary">
                    {label}
                  </Box>
                </div>
              ))}
              <div className="tz-grid-legend-item">
                <span className="tz-grid-legend-dot" />
                <Box variant="small" color="text-body-secondary">
                  Current Hour
                </Box>
              </div>
            </div>
          </>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};
